/**
 * @file Settings modal — design §7.
 * @module @labcraft/lab/ui/components/settings-modal
 *
 * Opened from the avatar dropdown's "Settings" item.
 * Three segmented controls, all persisted:
 *
 *   - **Appearance** (Light / Dark) — drives the shared theme mechanism
 *     (`@labcraft/ui`'s `setTheme`: `<html data-theme>` + cross-host
 *     cookie). No bespoke theme state.
 *   - **Default layout** (Terminal right / Terminal left) — read/writes
 *     the split orientation. `content-left` ⇒ terminal on the right;
 *     `content-right` ⇒ terminal on the left (see split_layout.tsx). The
 *     setter flips the *live* reader because the reader threads its
 *     orientation state down through `ReaderChromeContext`; outside the
 *     reader the orientation setter is a no-op persist (the control is
 *     hidden when no reader chrome is present).
 *   - **Reduce motion** — persists `labcraft.reduceMotion` and toggles
 *     `<html data-reduce-motion>`, which the new chrome gates its
 *     entrance animations on (in addition to the OS
 *     `prefers-reduced-motion` query).
 *
 * **Reader density** (Comfortable / Compact) is an OPEN decision in
 * umbrella #927 and is deliberately deferred — not rendered here.
 *
 * Closes on outside-click, the close button, or Escape. Entrance motion
 * (.17s, transform-only) is gated by both the OS media query and
 * `[data-reduce-motion='true']` in the CSS module.
 */

import { useEffect, useRef, useState, type JSX } from 'react'
import { readCurrentTheme, setTheme, type Theme } from '@labcraft/ui'
import type { SplitOrientation } from './split_layout.tsx'
import { readReduceMotion, writeReduceMotion } from '../lib/reduce_motion.ts'
import styles from './settings_modal.module.css'

/**
 * Props for {@link SettingsModal}.
 *
 * @property orientation - The live split orientation, or `null` outside
 *   the reader (the "Default layout" control is hidden when `null`).
 * @property onOrientationChange - Set the orientation. In the reader
 *   this flips the live layout via `ReaderChromeContext`; `null` when
 *   no reader is mounted.
 * @property onClose - Dismiss the modal.
 */
export type SettingsModalProps = {
  orientation: SplitOrientation | null
  onOrientationChange: ((next: SplitOrientation) => void) | null
  onClose: () => void
}

/**
 * Settings modal. See the file header for the per-control persistence
 * contract and the deferred "Reader density" decision.
 *
 * @param props - {@link SettingsModalProps}.
 * @returns The modal element.
 */
export function SettingsModal(props: SettingsModalProps): JSX.Element {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [theme, setThemeState] = useState<Theme>(() => readCurrentTheme())
  const [reduceMotion, setReduceMotionState] = useState<boolean>(() =>
    readReduceMotion(),
  )

  // Close on Escape; close on outside-click. The card stops propagation
  // so a click inside doesn't dismiss.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') props.onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [props])

  /** Apply + persist the chosen theme through the shared mechanism. */
  function chooseTheme(next: Theme): void {
    setThemeState(next)
    setTheme(next)
  }

  /** Apply + persist the reduce-motion preference. */
  function chooseReduceMotion(next: boolean): void {
    setReduceMotionState(next)
    writeReduceMotion(next)
  }

  // `content-left` ⇒ terminal right; `content-right` ⇒ terminal left.
  const terminalSide: 'right' | 'left' =
    props.orientation === 'content-right' ? 'left' : 'right'

  return (
    <div
      className={styles.scrim}
      role="dialog"
      aria-modal
      aria-labelledby="settings-title"
      onMouseDown={props.onClose}>
      <div
        className={styles.card}
        ref={cardRef}
        onMouseDown={e => e.stopPropagation()}>
        <header className={styles.head}>
          <h2 className={styles.title} id="settings-title">
            Settings
          </h2>
          <button
            className={styles.closeBtn}
            type="button"
            aria-label="Close settings"
            onClick={props.onClose}>
            ×
          </button>
        </header>

        <div className={styles.row}>
          <div className={styles.rowText}>
            <p className={styles.rowLabel}>Appearance</p>
            <p className={styles.rowHint}>Light paper or dark — your call.</p>
          </div>
          <div className={styles.segment} role="group" aria-label="Appearance">
            <button
              type="button"
              className={styles.seg}
              aria-pressed={theme === 'light'}
              onClick={() => chooseTheme('light')}>
              Light
            </button>
            <button
              type="button"
              className={styles.seg}
              aria-pressed={theme === 'dark'}
              onClick={() => chooseTheme('dark')}>
              Dark
            </button>
          </div>
        </div>

        {props.orientation && props.onOrientationChange && (
          <div className={styles.row}>
            <div className={styles.rowText}>
              <p className={styles.rowLabel}>Default layout</p>
              <p className={styles.rowHint}>
                Which side the terminal sits on in the reader.
              </p>
            </div>
            <div
              className={styles.segment}
              role="group"
              aria-label="Default layout">
              <button
                type="button"
                className={styles.seg}
                aria-pressed={terminalSide === 'left'}
                onClick={() => props.onOrientationChange?.('content-right')}>
                Terminal left
              </button>
              <button
                type="button"
                className={styles.seg}
                aria-pressed={terminalSide === 'right'}
                onClick={() => props.onOrientationChange?.('content-left')}>
                Terminal right
              </button>
            </div>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.rowText}>
            <p className={styles.rowLabel}>Reduce motion</p>
            <p className={styles.rowHint}>
              Turn off entrance animations across the lab.
            </p>
          </div>
          <button
            type="button"
            className={
              reduceMotion
                ? `${styles.toggle} ${styles.toggleOn}`
                : styles.toggle
            }
            role="switch"
            aria-checked={reduceMotion}
            aria-label="Reduce motion"
            onClick={() => chooseReduceMotion(!reduceMotion)}>
            <span className={styles.knob} aria-hidden />
          </button>
        </div>

        {/* Reader density (Comfortable / Compact) is an open decision in
            umbrella #927 — deferred, not rendered here. */}
      </div>
    </div>
  )
}
